/**
 * Billing interval of an object / the period an invoice covers (FACF-106).
 *
 * Wire contract (Api ADR-0021): lowercase strings. `yearly` means a standing invoice
 * („Dauerrechnung“, FAFD-27): its fixed amounts per payment on one invoice for a chosen service period;
 * extra work of such objects still goes on normal monthly invoices. `billingPeriodMonth` on an
 * invoice is the START month of the period (quarter: 1/4/7/10, year: 1).
 */
export const BILLING_INTERVALS = ['monthly', 'quarterly', 'yearly'] as const;

export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export const DEFAULT_BILLING_INTERVAL: BillingInterval = 'monthly';

export function isBillingInterval(value: unknown): value is BillingInterval {
  return typeof value === 'string' && (BILLING_INTERVALS as readonly string[]).includes(value);
}

/** Normalizes a wire value (any casing, null/undefined) to a known interval, defaulting to monthly. */
export function toBillingInterval(value?: string | null): BillingInterval {
  const normalized = (value ?? '').toLowerCase();
  return isBillingInterval(normalized) ? normalized : DEFAULT_BILLING_INTERVAL;
}

/** A `yearly` invoice is a standing invoice („Dauerrechnung“). */
export function isStandingInvoice(interval?: string | null): boolean {
  return toBillingInterval(interval) === 'yearly';
}

/**
 * Human-readable period label: `05/2026` (monthly), `Q2/2026` (quarterly), `2026` (yearly).
 * `startMonth` is the period's first month as delivered by the BE.
 */
export function formatBillingPeriod(
  year?: number | null,
  startMonth?: number | null,
  interval?: string | null,
): string {
  if (!year) return '—';
  const kind = toBillingInterval(interval);
  if (kind === 'yearly') {
    // why: legacy fallback without service dates — a standing invoice shows its months as a range,
    // e.g. 08/2026–07/2027 or 01/2026–12/2026 (FAFD-27).
    const start = startMonth && startMonth >= 1 && startMonth <= 12 ? startMonth : 1;
    const pad = (m: number) => String(m).padStart(2, '0');
    const endMonth = start === 1 ? 12 : start - 1;
    const endYear = start === 1 ? year : year + 1;
    return `${pad(start)}/${year}–${pad(endMonth)}/${endYear}`;
  }
  if (!startMonth) return '—';
  if (kind === 'quarterly') {
    return `Q${Math.floor((startMonth - 1) / 3) + 1}/${year}`;
  }
  return `${String(startMonth).padStart(2, '0')}/${year}`;
}

/**
 * Month range of a service period as delivered by the BE (`YYYY-MM-DD`), e.g. `10/2026–12/2026`.
 * Null when a date is missing or malformed.
 */
export function formatServiceMonths(from?: string | null, to?: string | null): string | null {
  const month = (d?: string | null) => {
    const m = d?.match(/^(\d{4})-(\d{2})/);
    return m ? `${m[2]}/${m[1]}` : null;
  };
  const start = month(from);
  const end = month(to);
  return start && end ? `${start}–${end}` : null;
}

/**
 * Period label of an invoice: a standing invoice shows its service months — it may cover less
 * than 12 months when the offer starts within the term (FAFD-27); every other invoice its period.
 */
export function formatInvoicePeriod(invoice: {
  billingPeriodYear?: number | null;
  billingPeriodMonth?: number | null;
  billingInterval?: string | null;
  serviceDateFrom?: string | null;
  serviceDateTo?: string | null;
}): string {
  const standing = isStandingInvoice(invoice.billingInterval)
    ? formatServiceMonths(invoice.serviceDateFrom, invoice.serviceDateTo)
    : null;
  return standing ?? formatBillingPeriod(invoice.billingPeriodYear, invoice.billingPeriodMonth, invoice.billingInterval);
}

/** i18n key of the interval label; the labels live in `common.json` so objects and invoices share them. */
export function billingIntervalLabelKey(interval?: string | null): string {
  return `common:billingInterval.${toBillingInterval(interval)}`;
}

/**
 * How an object is invoiced (FAFD-27): monthly invoices or a standing invoice („Dauerrechnung“,
 * `yearly` on the wire). Quarterly invoicing of objects is retired — `quarterly` only still
 * appears on old invoices.
 */
export const OBJECT_BILLING_INTERVALS = ['monthly', 'yearly'] as const;

/** How often the customer pays against a standing invoice (Api `PaymentInterval`). */
export const PAYMENT_INTERVALS = ['monthly', 'quarterly', 'halfYearly', 'yearly'] as const;
export type PaymentInterval = (typeof PAYMENT_INTERVALS)[number];

export function toPaymentInterval(value?: string | null): PaymentInterval {
  return PAYMENT_INTERVALS.find((p) => p.toLowerCase() === (value ?? '').toLowerCase()) ?? 'monthly';
}

/** i18n key (namespace `common`) of a payment interval, e.g. „vierteljährlich“. */
export function paymentIntervalLabelKey(interval?: string | null): string {
  return `common:paymentInterval.${toPaymentInterval(interval)}`;
}

/** How an object is invoiced, as the object DTOs carry it (FAFD-27). */
export interface ObjectBilling {
  billingInterval?: string | null;
}

/**
 * Label of how an object is invoiced: „Monatsabrechnung“ or „Dauerrechnung“. Period and payment
 * interval of a standing invoice are chosen when it is created, not on the object (FAFD-27).
 */
export function objectBillingLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  billing: ObjectBilling,
): string {
  return t(isStandingInvoice(billing.billingInterval) ? 'objects:billingMode.yearly' : 'objects:billingMode.monthly');
}
